import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { gql } from 'graphql-request'
import { ArrowPathIcon, SparklesIcon, DocumentDuplicateIcon, CheckIcon, BeakerIcon } from '@heroicons/react/24/outline'
import { MessageSquareText } from 'lucide-react'
import { useState } from 'react'

import { useMutation } from '@hooks/graphql'

import { Button } from '@/components/ui/button'
import CircleSpinner from '@components/CircleSpinner'
import SecureMarkdown from '@components/SecureMarkdown'
import TextEditor from '@components/TextEditor'
import DisplayAttachment from '@components/DisplayAttachment/DisplayAttachment'
import Collapsible from '@components/Collapsible'
import Label from '@components/Label'
import CameraCapture from '@components/CameraCapture'
import { useCurrentUser } from '@contexts/currentUser'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog'

const PREVIEW_FREE_RESPONSE_AUTOMARKING_MUTATION = gql`
  mutation PreviewFreeResponseAutoMarking($input: PreviewFreeResponseAutoMarkingInput!) {
    previewFreeResponseAutoMarking(input: $input) {
      marks
      feedback
      reasoning
      detailedReasoning
      markSchemeSuggestions

      errors {
        message
      }
    }
  }
`

const UPLOAD_ATTACHMENTS_MUTATION = gql`
  mutation uploadEducatorProjectAttachments($input: UploadEducatorProjectAttachmentsInput!) {
    uploadEducatorProjectAttachments(input: $input) {
      attachments {
        id
        url
        filename
      }
      errors {
        message
      }
    }
  }
`

const MAX_ATTACHMENTS = 10
const MAX_PDF_ATTACHMENTS = 3

const PreviewFreeResponseQuestionDialog = ({ blockId, getValues }) => {
  const { question, markAllocation, markScheme } = getValues()
  const [didMarkingFail, setDidMarkingFail] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 1000)
  }

  const {
    mutateAsync: previewFreeResponseAutoMarking,
    isLoading: isMarking,
    isSuccess: isMarkingComplete,
    data: { previewFreeResponseAutoMarking: { marks, feedback, reasoning, detailedReasoning, markSchemeSuggestions, errors: markingErrors = [] } = {} } = {},
    reset: resetMarking
  } = useMutation({
    gqlMutation: PREVIEW_FREE_RESPONSE_AUTOMARKING_MUTATION,
    onError: () => {
      setError('submission', { message: 'Marking failed for an unknown reason. Please try again or contact support.' })
      setDidMarkingFail(true)
    },
    onSuccess: (data) => {
      if (data?.previewFreeResponseAutoMarking?.errors?.length > 0) {
        const errorMessage = data.previewFreeResponseAutoMarking.errors[0].message
        setError('submission', { message: errorMessage || 'An error occurred with marking. Please try again or contact support.' })
        setDidMarkingFail(true)
      } else {
        clearErrors('submission')
      }
    }
  })

  const { mutateAsync: uploadAttachments, isLoading: isUploading } = useMutation({
    gqlMutation: UPLOAD_ATTACHMENTS_MUTATION
  })

  const { handleSubmit, formState: { errors }, control, reset: resetForm, setError, clearErrors, watch } = useForm({
    mode: 'onTouched'
  })

  const { fields, append, remove: removeAttachments } = useFieldArray({
    control,
    name: 'attachments'
  })

  const submittedAnswer = watch('answer')
  const attachments = watch('attachments')

  // Clear submission error when answer changes
  const handleAnswerChange = (value) => {
    clearErrors('submission')
    return value
  }

  const submit = ({ answer, attachments = [] }) => {
    clearErrors('submission')
    setDidMarkingFail(false)
    const attachmentIds = attachments.map(attachment => attachment.id)
    previewFreeResponseAutoMarking({ input: { id: blockId, answer, attachmentIds, question, markAllocation, markScheme } })
  }

  const handleFormReset = () => {
    resetMarking()
    resetForm()
    removeAttachments()
    clearErrors('submission')
    setDidMarkingFail(false)
  }

  // Modified to clear submission error
  const handleRemoveAttachment = (id) => {
    removeAttachments(id)
    clearErrors('submission')
  }

  const handleFilesAttached = async event => {
    clearErrors() // This clears all errors including submission

    const files = Array.from(event.target.files)

    // Check that files are either images or PDFs
    // File pickers can allow selecting other file types on some devices.
    if (files.some(file => !file.type.startsWith('image/') && file.type !== 'application/pdf')) {
      setError('attachments', { message: 'Files must be images or PDFs.' })
      return
    }

    // File size limit
    if (files.some(file => file.size > 10000000)) { // 10mb limit
      setError('attachments', { message: 'Files must be less than 10MB in size.' })
      return
    }

    // Count PDFs in the current upload
    const pdfCount = files.filter(file => file.type === 'application/pdf').length
    if (pdfCount > MAX_PDF_ATTACHMENTS) {
      setError('attachments', { message: `You can only upload up to ${MAX_PDF_ATTACHMENTS} PDF files at a time.` })
      return
    }

    // Check total attachment limit (10)
    if (fields.length + files.length > MAX_ATTACHMENTS) {
      setError('attachments', { message: `You can only have a maximum of ${MAX_ATTACHMENTS} attachments in total.` })
      return
    }

    const { uploadEducatorProjectAttachments: { attachments = [], errors = [] } } = await uploadAttachments({ input: { attachments: files } })

    if (errors.length > 0) {
      setError('attachments', { message: errors[0].message })
    } else {
      append(attachments)
      clearErrors('submission') // Clear submission error on successful upload
    }
  }

  // This function is called when the dialog's open state changes to false
  const onDialogClose = (open) => {
    if (!open) {
      handleFormReset()
      // Note: The original closeModal prop is no longer here to call.
      // If FreeResponseQuestionForm needs to know it closed, a new prop callback would be needed.
    }
  }

  const { currentMembership: { organization: { features = [] } = {} } = {} } = useCurrentUser()

  const advancedMarkingEnabled = features.includes('advancedMarking')

  return (
    <Dialog onOpenChange={onDialogClose}>
      <DialogTrigger asChild>
        <Button variant='outline' type='button'>
          <span className='flex gap-2 items-center whitespace-nowrap'>
            <BeakerIcon className='h-5 w-5' />
            <span className='hidden sm:inline'>Preview question</span>
            <span className='sm:hidden'>Preview</span>
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>
            <div className='flex items-center'>
              <MessageSquareText className='size-8 mr-3 bg-orange-200 text-orange-900 rounded-md p-1 shrink-0' />
              <h3 className='text-2xl font-bold leading-none'>Free Response Preview</h3>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className='w-full flex flex-col px-3 py-5 gap-5 max-h-[70vh] overflow-y-auto'> {/* Adjusted max-h slightly for footer room */}
          <SecureMarkdown content={question} />

          <Choose>
            <When condition={isMarkingComplete && !errors.submission && !didMarkingFail}>
              <div className='bg-muted p-3 rounded-lg'>
                <SecureMarkdown content={submittedAnswer} />

                <If condition={attachments?.length > 0}>
                  <div className='flex flex-wrap gap-3'>
                    <For each='attachment' of={attachments}>
                      <DisplayAttachment
                        key={attachment.id}
                        id={attachment.id}
                        filename={attachment.filename}
                        url={attachment.url}
                        outline={false}
                      />
                    </For>
                  </div>
                </If>
              </div>

              <div>
                <Label title='Mark' />
                <div className='w-fit rounded-lg leading-tight'>
                  <span className='text-lg font-semibold'>{marks} / {markAllocation}</span>
                </div>
              </div>

              <div className='flex bg-purple-50 dark:bg-purple-900/50 p-3 rounded-lg leading-tight'>
                <SparklesIcon className='size-5 block mr-2 shrink-0 text-purple-600 dark:text-purple-400' />
                <SecureMarkdown content={reasoning} />
              </div>

              <div>
                <Label title='Student feedback' />
                <div className='flex flex-col rounded-lg leading-tight'>
                  <SecureMarkdown content={feedback} />
                </div>
              </div>

              <If condition={advancedMarkingEnabled}>
                <Collapsible
                  label={
                    <div className='flex items-center gap-2'>
                      Advanced Marking
                    </div>
                  }
                >
                  <div className='flex flex-col gap-4'>
                    <If condition={detailedReasoning}>
                      <div className='bg-purple-50 dark:bg-purple-900/50 p-3 rounded-lg relative'>
                        <div className='flex items-center mb-3'>
                          <SparklesIcon className='size-5 block mr-2 shrink-0 text-purple-600 dark:text-purple-400' />
                          <h3 className='text-lg font-semibold'>Advanced AI reasoning</h3>
                        </div>

                        <button
                          aria-label='Copy text to clipboard'
                          className='absolute top-3 right-3 rounded-md p-1 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-700'
                          onClick={() => copyToClipboard(detailedReasoning)}
                          type='button'
                        >
                          <Choose>
                            <When condition={isCopied}>
                              <CheckIcon className='h-5 w-5' />
                            </When>
                            <Otherwise>
                              <DocumentDuplicateIcon className='h-5 w-5' />
                            </Otherwise>
                          </Choose>
                        </button>

                        <SecureMarkdown content={detailedReasoning} />
                      </div>
                    </If>

                    <Choose>
                      <When condition={Array.isArray(markSchemeSuggestions) && markSchemeSuggestions.length > 0}>
                        <div className='flex flex-col bg-purple-50 dark:bg-purple-900/50 p-3 rounded-lg leading-tight'>
                          <div className='flex items-center mb-1'>
                            <SparklesIcon className='size-5 block mr-2 shrink-0 text-purple-600 dark:text-purple-400' />
                            <h3 className='text-lg font-semibold'>Mark scheme suggestions</h3>
                          </div>
                          <ul className='list-disc pl-5 space-y-1'>
                            <For each='suggestion' index='index' of={markSchemeSuggestions}>
                              <li key={index}>
                                <SecureMarkdown content={suggestion} />
                              </li>
                            </For>
                          </ul>
                        </div>
                      </When>
                      <When condition={typeof markSchemeSuggestions === 'string' && markSchemeSuggestions.trim() !== ''}>
                        <div className='flex flex-col bg-muted p-3 rounded-lg leading-tight'>
                          <div className='flex items-center mb-1'>
                            <SparklesIcon className='size-5 block mr-2 shrink-0 text-purple-600 dark:text-purple-400' />
                            <h3 className='text-lg font-semibold'>Mark scheme suggestions</h3>
                          </div>
                          <SecureMarkdown content={markSchemeSuggestions} />
                        </div>
                      </When>
                    </Choose>
                  </div>
                </Collapsible>
              </If>

              <DialogFooter className='mt-4'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => handleFormReset()}
                >
                  <span className='flex gap-2'><ArrowPathIcon className='h-5 w-5' /> Reset Preview</span>
                </Button>
              </DialogFooter>
            </When>

            <Otherwise>
              <form onSubmit={handleSubmit(submit)} className='flex flex-col flex-grow gap-3'>
                <div className='flex-grow space-y-3'>
                  <TextEditor.Container>
                    <If condition={fields.length > 0}>
                      <TextEditor.AttachmentList>
                        <For each='attachment' of={fields}>
                          <TextEditor.Attachment
                            key={attachment.id}
                            id={attachment.id}
                            filename={attachment.filename}
                            url={attachment.url}
                            remove={() => handleRemoveAttachment(attachment.id)}
                          />
                        </For>
                      </TextEditor.AttachmentList>
                    </If>

                    <Controller
                      name='answer'
                      rules={{
                        validate: value => (value || fields.length > 0) ? null : 'Answer or attachment is required'
                      }}
                      control={control}
                      render={({ field }) => (
                        <TextEditor
                          className='min-h-[150px]'
                          {...field}
                          onChange={(value) => {
                            clearErrors('submission')
                            field.onChange(value)
                          }}
                        />
                      )}
                    />

                    <TextEditor.Actions>
                      <Choose>
                        <When condition={isUploading}>
                          <CircleSpinner className='p-1 mr-2' />
                        </When>

                        <Otherwise>
                          <TextEditor.FileInput
                            disabled={isMarking}
                            onChange={handleFilesAttached}
                            accept='image/*,application/pdf'
                            fileType='document'
                          />

                          <TextEditor.FileInput
                            disabled={isMarking}
                            onChange={handleFilesAttached}
                            accept='image/*,application/pdf'
                            fileType='image'
                          />

                          <CameraCapture
                            onCapture={async file => await handleFilesAttached({ target: { files: [file] } })}
                            disabled={isMarking}
                          />
                        </Otherwise>
                      </Choose>

                      <TextEditor.ToolbarToggle />
                    </TextEditor.Actions>
                  </TextEditor.Container>

                  <If condition={errors.answer}>
                    <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.answer.message}</p>
                  </If>

                  <If condition={errors.attachments}>
                    <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.attachments.message}</p>
                  </If>

                  <If condition={errors.submission}>
                    <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.submission.message}</p>
                  </If>
                </div>

                <DialogFooter className='sm:justify-between pt-3'>
                  <DialogClose asChild>
                    <Button type='button' variant='outline'>Cancel</Button>
                  </DialogClose>
                  <Choose>
                    <When condition={isMarking}>
                      <Button className='ml-auto' disabled>
                        <CircleSpinner className='h-5 w-5 mr-2' /> Marking
                      </Button>
                    </When>
                    <Otherwise>
                      <Button
                        type='submit'
                        disabled={isMarking}
                      >
                        Submit
                      </Button>
                    </Otherwise>
                  </Choose>
                </DialogFooter>
              </form>
            </Otherwise>
          </Choose>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PreviewFreeResponseQuestionDialog
